// filepath: apps/web/src/admin/positions/api.ts
//
// ISS-516 — typed REST client for /api/directory/positions/*.
//
// Mirrors the wire shape at internal/directory/position/handlers.go
// (RegisterRoutes, handlers.go:62-79). The Go handler exposes 6
// routes; the SPA package wraps each one with typed inputs/outputs,
// the canonical `apiFetch` client (bearer header + 401 silent-
// refresh retry chain), and INV-8 idempotency for every mutation.
//
// INV-2: server is the single source of truth on what is
// visible. The Go handler at handlers.go:174,210 uses
// `authorization.WriteStripped(..., "position", out)` -- the SPA
// MUST NOT synthesise display fields the server did not emit
// (FR-AUTH-6 / `entity.Authorizer.StripFields`).
//
// INV-7: every mutation carries `row_version` (OCC). The
// row_version the client sends is the one echoed back by the
// last GET response; the server rejects with 409 on mismatch.
//
// INV-8: every mutating call carries `X-Idempotency-Key`. The
// helper `newIdempotencyKey()` is the canonical mint (the same
// helper used by features/records/comments/api.ts and
// admin/counterparties/api.ts).
//
// INV-9: every value in error envelopes is rendered as JSX text;
// the SPA must never `dangerouslySetInnerHTML` a server-sent
// string.
//
// ISS-516 BA-corrected wire (per the #516 SPA <-> Go diff note  // design-adherence-ignore
// 17855): Position is the Go wire type from
// `internal/directory/position/package.go:22-40`. The wire
// enum for status is ("open"|"filled"|"closed") — pre-ISS-516
// the SPA carried the wrong enum ("active"|"closed") and
// three phantom fields (role_code, unit_name, updated_at) the
// Go server never sent.

import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";
import type { Position } from "@/features/directory/types";

/**
 * Wire shape of a Position record as returned by the backend.
 * Alias of `features/directory/types.ts#Position` so the admin
 * module and the features module agree without a circular
 * re-export (the admin form imports the API; the API imports
 * the features module's Position).
 */
export type { Position };

/**
 * Input for `createPosition` (no id / row_version / audit fields).
 *
 * ISS-516 BA-corrected (per the #516 SPA <-> Go diff note  // design-adherence-ignore
 * 17855): the wire name for the position code is `job_code`
 * (Go struct: JobCode *string, JSON: "job_code"). The pre-ISS-516
 * SPA used `code` which is the wrong wire name.
 */
export interface CreatePositionInput {
  organization_unit_id?: string | null;
  location_id?: string | null;
  title: string;
  job_code?: string | null;
}

/**
 * Input for `updatePosition` -- INV-7 row_version is mandatory.
 */
export interface UpdatePositionInput {
  organization_unit_id?: string | null;
  location_id?: string | null;
  title?: string;
  job_code?: string | null;
  row_version: number;
}

/** Input for `archivePosition` -- INV-7 row_version is mandatory. */
export interface ArchivePositionInput {
  row_version: number;
}

/** Optional parameters for `listPositions`. */
export interface ListPositionsOpts {
  /** Filter to positions in a specific org unit. */
  organization_unit_id?: string;
  /** Filter to positions at a specific location. */
  location_id?: string;
  /** When true, include status='closed' positions. Default false. */
  include_closed?: boolean;
  /** Page size (Go caps at 200, default 50). */
  limit?: number;
  /** Page offset (Go: default 0). */
  offset?: number;
}

/** Wire envelope shape returned by `GET /api/directory/positions`. */
export interface PositionsListEnvelope {
  records: Position[];
  total: number;
}

/**
 * Generate a v4 UUID via crypto.randomUUID() with a Date.now
 * fallback. Mirror of admin/counterparties/api.ts#newIdempotencyKey
 * and admin/locations/api.ts#newIdempotencyKey.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * callApi -- thin wrapper around `apiFetch` that throws
 * `ApiError(message, status, code)` on non-2xx responses,
 * handles the 204 No Content body, and forwards an explicit
 * `X-Idempotency-Key` header (INV-8).
 *
 * Loosely modeled on `admin/locations/api.ts#callApi` -- same
 * wire contract (status + code + message envelope).
 */
async function callApi<T>(
  url: string,
  opts: {
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const resp = await apiFetch(url, {
    method: opts.method ?? "GET",
    init,
    idempotencyKey: opts.idempotencyKey,
  });
  if (!resp.ok) {
    let code = "";
    let message = `HTTP ${resp.status}`;
    try {
      const errBody = (await resp.json()) as {
        code?: string;
        message?: string;
        error?: string;
      };
      if (typeof errBody.code === "string") code = errBody.code;
      if (typeof errBody.message === "string") message = errBody.message;
      else if (typeof errBody.error === "string") message = errBody.error;
    } catch {
      /* not JSON, fall back to status string */
    }
    throw new ApiError(message, resp.status, code);
  }
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  return (await resp.json()) as T;
}

const BASE = "/api/directory/positions";

/**
 * POST /api/directory/positions -- 201 + the created row.
 *
 * The Go handler at handlers.go:110 reads `title` (required)
 * and accepts `organization_unit_id` / `location_id` /
 * `job_code` as optional. The server validates `title` via
 * position.Validate and returns 400 on empty title.
 */
export async function createPosition(
  input: CreatePositionInput,
  idempotencyKey: string,
): Promise<Position> {
  // Build the body with only the fields the user actually
  // provided. The Go struct uses `*uuid.UUID` /
  // `*string` (nullable, omitempty) for the optional fields;
  // omit them so the Go decoder sees absent (not null).
  const body: Record<string, unknown> = { title: input.title };
  if (input.organization_unit_id) {
    body.organization_unit_id = input.organization_unit_id;
  }
  if (input.location_id) {
    body.location_id = input.location_id;
  }
  if (input.job_code) {
    body.job_code = input.job_code;
  }
  return callApi<Position>(BASE, {
    method: "POST",
    body,
    idempotencyKey,
  });
}

/**
 * GET /api/directory/positions -- list envelope.
 * The Go server at handlers.go:253 calls
 * `authorization.WriteStrippedList(..., "position", out,
 * "records")`, producing the canonical `{ records, total }`
 * shape.
 */
export async function listPositions(
  opts: ListPositionsOpts = {},
): Promise<PositionsListEnvelope> {
  const qs = new URLSearchParams();
  if (opts.organization_unit_id) {
    qs.set("organization_unit_id", opts.organization_unit_id);
  }
  if (opts.location_id) {
    qs.set("location_id", opts.location_id);
  }
  if (opts.include_closed) {
    qs.set("include_closed", "true");
  }
  if (opts.limit !== undefined) {
    qs.set("limit", String(opts.limit));
  }
  if (opts.offset !== undefined) {
    qs.set("offset", String(opts.offset));
  }
  const url = qs.size > 0 ? `${BASE}?${qs}` : BASE;
  return callApi<PositionsListEnvelope>(url);
}

/**
 * GET /api/directory/positions/{id} -- single record.
 * Returns null on 404 (consistent with the Locations pattern).
 */
export async function getPosition(id: string): Promise<Position | null> {
  try {
    return await callApi<Position>(`${BASE}/${encodeURIComponent(id)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * PATCH /api/directory/positions/{id} -- INV-7 row_version is
 * mandatory in the body. The server returns 409
 * row_version_mismatch on stale submission and the SPA
 * surfaces a StaleVersionError (reload to fetch fresh
 * row_version).
 */
export async function updatePosition(
  id: string,
  input: UpdatePositionInput,
  idempotencyKey: string,
): Promise<Position> {
  // The Go updateReq shape (handlers.go:96-99):
  //   { "row_version": N, "fields": { ... } }
  // The inner "fields" wrapper is the Go convention so the
  // server can distinguish "the client wants to update X to
  // null" from "the client is not sending X at all".
  const fields: Record<string, unknown> = {};
  if (input.organization_unit_id !== undefined) {
    fields.organization_unit_id = input.organization_unit_id;
  }
  if (input.location_id !== undefined) {
    fields.location_id = input.location_id;
  }
  if (input.title !== undefined) {
    fields.title = input.title;
  }
  if (input.job_code !== undefined) {
    fields.job_code = input.job_code;
  }
  const body = { row_version: input.row_version, fields };
  return callApi<Position>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body,
    idempotencyKey,
  });
}

/**
 * POST /api/directory/positions/{id}/close -- archive.
 *
 * The Go closeReq shape (handlers.go:101-103):
 *   { "row_version": N }
 *
 * The SPA-facing verb is "Archive" (per BA spec; matches the
 * Locations pattern). On success the Go handler returns the
 * updated Position (with status: "closed", closed_at set).
 */
export async function archivePosition(
  id: string,
  input: ArchivePositionInput,
  idempotencyKey: string,
): Promise<Position> {
  return callApi<Position>(`${BASE}/${encodeURIComponent(id)}/close`, {
    method: "POST",
    body: { row_version: input.row_version },
    idempotencyKey,
  });
}
